import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { GooglePlayIcon, Apple } from "../../assets/DirectTopUpIcon";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { fetchProviderFaqs } from "../../features/store/providerFaqSlice";

const TopUpDetails = () => {
  const tabItems = ["Details", "FAQs"];
  const [toggle, setToggle] = useState<string>(tabItems[0] || "Details");

  const dispatch = useAppDispatch();
  const location = useLocation();
  const { data: appData } = useAppSelector((state) => state.appData);
  const { faqs, loading: faqLoading, error: faqError } = useAppSelector(
    (state) => state.providerFaqs,
  );

  // Extract the last path segment as the category key
  const path = location.pathname;
  const segments = path.split("/").filter(Boolean); // ["products", "data", "MTN"]
  const formattedProduct =
    segments.length >= 2 ? segments[segments.length - 2] : "";
  const formattedProvider =
    segments.length >= 1 ? segments[segments.length - 1] : "";

  const providers = Array.isArray(appData[formattedProduct]?.providers)
    ? appData[formattedProduct].providers
    : [];

  const provider = providers.find(
    (item: { slug: string }) => item.slug === formattedProvider,
  );

  const providerKey = useMemo(
    () => `${formattedProduct}-${provider?.slug ?? ""}`,
    [formattedProduct, provider?.slug],
  );

  useEffect(() => {
    if (!provider?.slug || !formattedProduct) return;

    dispatch(
      fetchProviderFaqs({
        productKey: formattedProduct,
        providerSlug: provider.slug,
      }),
    );
  }, [provider?.slug, formattedProduct, dispatch]);

  return (
    provider && (
      <div
        className={`w-full bg-white  bp:w-[200px] lg:w-[250px] xl:w-[300px] flex flex-col gap-4 bp:gap-2 lg:gap-4 dark:bg-[black] `}
      >
        <img
          src={provider.image_url}
          alt={formattedProvider}
          className="w-full h-[232px] rounded-lg"
        />

        {tabItems.length > 0 && (
          <div className="rounded-lg p-[6px] row-2 bg-[#F4F4F5] dark:bg-BG-dark w-full h-[42px]">
            {tabItems.map((item, index) => (
              <button
                key={index}
                className={`flex-1 py-[6px] px-[6px] bp:px-[10px] w-[140px] bp:w-[153px] h-[34px] text-xs place-content-center ${
                  toggle === item
                    ? "inter-xs-semibold-dark rounded-lg font-inter dark:text-white bg-[#FCFCFD] dark:bg-[#FFFFFF1A]"
                    : "inter-xs-medium-toggleBG-dark dark:text-main-dark"
                }`}
                onClick={() => setToggle(item)}
              >
                {item}
              </button>
            ))}
          </div>
        )}

        {toggle === "Details" ? (
          <div className="flex flex-col gap-4 dark:text-white">
            <p className="text-xl font-semibold font-lt">{provider.name}</p>
            {provider.description && (
              <p className="text-sm font-satoshi">{provider.description}</p>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-3 dark:text-white">
            <p className="text-xl font-semibold font-lt">{provider.name} FAQs</p>
            {faqLoading ? (
              <p className="text-sm font-satoshi">Loading FAQs...</p>
            ) : faqError ? (
              <p className="text-sm font-satoshi text-red-500">{faqError}</p>
            ) : faqs.length > 0 ? (
              faqs.map((faq) => (
                <div
                  key={`${providerKey}-${faq.id}`}
                  className="rounded-lg border border-[#E4E7EC] dark:border-[#FFFFFF1A] p-3"
                >
                  <p className="text-sm font-semibold font-lt mb-2">
                    {faq.question}
                  </p>
                  <div
                    className="text-sm font-satoshi [&_a]:text-blue-600 dark:[&_a]:text-blue-400 [&_a]:no-underline"
                    dangerouslySetInnerHTML={{ __html: faq.answer }}
                  />
                </div>
              ))
            ) : (
              <p className="text-sm font-satoshi">No FAQs available.</p>
            )}
          </div>
        )}
        <div>
          {provider.google_url ||
            (provider.google_url && (
              <p className="text-sm font-semibold mb-2 font-lt">Get the game</p>
            ))}
          <div className="flex items-center gap-2">
            {provider.google_url && (
              <a
                href={provider.google_url}
                className="bg-[#000000] flex gap-2 items-center w-full lg:w-[148.5px] h-[44px] dark:border-tabs rounded-md border-[1px] px-2"
              >
                <GooglePlayIcon />
                <div>
                  <p className="text-[8px] bp:text-[6px] lg:text-[8px] xl:text-[10px] text-white">
                    GET IT ON
                  </p>
                  <p className="text-xs bp:text-[7px] lg:text-[9px] xl:text-base text-white">
                    Google Play
                  </p>
                </div>
              </a>
            )}
            {provider.apple_url && (
              <a
                href={provider.apple_url}
                className="bg-[#000000] flex gap-2 items-center w-full lg:w-[148.5px] h-[44px] dark:border-tabs rounded-md border-[1px] px-2"
              >
                <Apple />
                <div>
                  <p className="text-[8px] bp:text-[6px] lg:text-[8px] xl:text-[10px] text-white">
                    Download on the{" "}
                  </p>
                  <p className="text-xs bp:text-[7px] lg:text-[10px] xl:text-base text-white">
                    App Stores
                  </p>
                </div>
              </a>
            )}
          </div>
        </div>
      </div>
    )
  );
};

export default TopUpDetails;
